import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    ArrowLeft,
    Layers,
    Clock,
    Play,
    MessageSquare,
    Repeat,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_LABELS } from "@/lib/workoutTaxonomy";
import { buildSteps, isLastStepInBlock, uniqueDrivers } from "@/lib/workoutSteps";
import FocusModeOverlay from "@/components/runner/FocusModeOverlay";
import BodyweightCard from "@/components/biometrics/BodyweightCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkoutRunner() {
    const { user, loading, activeAthleteId } = useAuth();
    const { workoutId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Combined mode: a program day with 2+ workouts opens them as ONE
    // continuous run (blocks concatenated). Detected by ?ids=w1,w2.
    const combinedIds = (searchParams.get("ids") || "").split(",").filter(Boolean);
    const isCombined = combinedIds.length >= 2;
    const idsKey = combinedIds.join(",");
    const [sourceWorkouts, setSourceWorkouts] = useState([]);

    const [workout, setWorkout] = useState(null);
    const [maxes, setMaxes] = useState({});
    const [equipment, setEquipment] = useState([]);
    const [exercises, setExercises] = useState([]);
    const [loadingData, setLoadingData] = useState(true);

    const [focusOpen, setFocusOpen] = useState(false);
    const [phase, setPhase] = useState("running"); // running | wheel | done
    // Phase 2 (2026-02-26): single linear cursor over the flattened step list.
    // The step builder handles interleaving for supersets/circuits, so the
    // runner stays a dumb walker — no nested block/slot/set indices.
    const [stepIdx, setStepIdx] = useState(0);
    // Map of base_exercise_id (= the Level 1 exercise_id of each slot) →
    // current level idx the athlete has graduated to. Loaded from
    // /api/me/exercise-progression so progress carries across workouts.
    // We compute `selectedLevels` (the {blockIdx_slotIdx: levelIdx} shape
    // workoutSteps.buildSteps wants) DERIVED from this map + the workout.
    const [progression, setProgression] = useState({});
    const [typedMaxRows, setTypedMaxRows] = useState([]);
    const [actuals, setActuals] = useState({});
    const [pendingDelta, setPendingDelta] = useState(0);
    const [setsCompleted, setSetsCompleted] = useState(0);
    const [startedAt, setStartedAt] = useState(null);
    const [sessionLogged, setSessionLogged] = useState(false);

    useEffect(() => {
        if (!loading && !user) navigate("/", { replace: true });
    }, [loading, user, navigate]);

    useEffect(() => {
        (async () => {
            try {
                const params = activeAthleteId ? { athlete_id: activeAthleteId } : {};
                // Core: workout + exercises. Stretch: maxes + equipment.
                // Soft-fail the stretch ones (accounts without an athlete
                // profile would otherwise 400 from _resolve_active_athlete_id
                // and bounce the runner back to /dashboard/workouts → loop).
                const workoutReq = isCombined
                    ? axios.get(`${API}/workouts/combined`, { params: { ...params, ids: idsKey }, withCredentials: true })
                    : axios.get(`${API}/workouts/${workoutId}`, { params, withCredentials: true });
                const [w, ex] = await Promise.all([
                    workoutReq,
                    axios.get(`${API}/exercises`, { withCredentials: true }),
                ]);
                setWorkout(w.data);
                setSourceWorkouts(w.data?.source_workouts || (workoutId ? [{ workout_id: workoutId, title: w.data?.title }] : []));
                setExercises(ex.data || []);
                const [mRes, eqRes, progRes, tRes] = await Promise.all([
                    axios.get(`${API}/me/maxes`, { params, withCredentials: true }).catch(() => ({ data: {} })),
                    axios.get(`${API}/equipment`, { withCredentials: true }).catch(() => ({ data: [] })),
                    axios.get(`${API}/me/exercise-progression`, { params, withCredentials: true }).catch(() => ({ data: {} })),
                    axios.get(`${API}/me/workout-maxes`, { params, withCredentials: true }).catch(() => ({ data: { maxes: [] } })),
                ]);
                setMaxes(mRes.data || {});
                setEquipment(eqRes.data || []);
                setProgression(progRes.data || {});
                setTypedMaxRows(tRes.data?.maxes || []);
            } catch (e) {
                toast.error("Couldn't load workout", { description: e?.response?.data?.detail });
                if (window.history.length > 1) navigate(-1);
                else navigate("/dashboard", { replace: true });
            } finally {
                setLoadingData(false);
            }
        })();
    }, [workoutId, idsKey, isCombined, navigate, activeAthleteId]);

    const equipmentMap = useMemo(
        () => Object.fromEntries(equipment.map((eq) => [eq.equipment_id, eq])),
        [equipment],
    );
    const exerciseMap = useMemo(
        () => Object.fromEntries(exercises.map((ex) => [ex.exercise_id, ex])),
        [exercises],
    );

    // Derive the per-slot level selection from the athlete's persisted
    // progression map. Key = base (Level 1) exercise_id of each slot.
    // If the athlete graduated to Level 3 on Box Squat, every workout slot
    // whose Level 1 is "Box Squat" auto-picks Level 3 (clamped to the
    // number of levels the workout actually defines).
    const selectedLevels = useMemo(() => {
        const out = {};
        const blocks = workout?.blocks || [];
        blocks.forEach((b, bi) => {
            (b.exercises || []).forEach((slot, si) => {
                const levels = slot.levels || [];
                if (!levels.length) return;
                const baseExId = levels[0]?.exercise_id;
                const desired = progression[baseExId];
                if (typeof desired === "number" && desired > 0) {
                    // Clamp to what's actually defined in the workout.
                    out[`${bi}_${si}`] = Math.min(desired, levels.length - 1);
                }
            });
        });
        return out;
    }, [workout, progression]);

    // Flattened step list — interleaves supersets/circuits by round.
    const steps = useMemo(
        () => (workout ? buildSteps(workout, selectedLevels) : []),
        [workout, selectedLevels],
    );

    const currentStep = steps[stepIdx] || null;
    const blockIdx = currentStep?.blockIdx ?? 0;

    // Drivers required for max-gating: 1 entry per unique exercise across
    // the WHOLE workout (not just the current step). Used by the read-only
    // map below; the inline-max prompt inside the overlay is per-step.
    const requiredDrivers = useMemo(
        () => uniqueDrivers(steps, exerciseMap),
        [steps, exerciseMap],
    );
    const missingMaxes = requiredDrivers.filter((ex) => !(maxes[ex.exercise_id]?.value > 0));

    // Save a single (or multi) max inline during the workout. Backend upserts
    // by (athlete_id, exercise_id) so the same exercise across other workouts
    // automatically picks up the new value for THIS kid (siblings stay isolated).
    const saveMaxInline = async (maxesPayload) => {
        const r = await axios.put(
            `${API}/me/maxes`,
            { maxes: maxesPayload },
            { params: activeAthleteId ? { athlete_id: activeAthleteId } : {}, withCredentials: true },
        );
        setMaxes(r.data || {});
        return r.data || {};
    };

    // Typed maxes (2026-06, Sachin: "some of them have 2 maxes") — exercises
    // declare maxes_config (time_secs, speed_mph, ...); each type is its own
    // user_maxes row. Grouped by exercise for the runner's max panel.
    const typedMaxes = useMemo(() => {
        const out = {};
        typedMaxRows.forEach((r) => {
            if (!out[r.exercise_id]) out[r.exercise_id] = [];
            out[r.exercise_id].push(r);
        });
        return out;
    }, [typedMaxRows]);

    const saveTypedMax = async (exerciseId, exerciseName, maxType, weight) => {
        const params = activeAthleteId ? { athlete_id: activeAthleteId } : {};
        await axios.post(
            `${API}/me/workout-maxes`,
            { exercise_id: exerciseId, exercise_name: exerciseName, max_type: maxType, max_weight: weight },
            { params, withCredentials: true },
        );
        const [mRes, tRes] = await Promise.all([
            axios.get(`${API}/me/maxes`, { params, withCredentials: true }).catch(() => ({ data: {} })),
            axios.get(`${API}/me/workout-maxes`, { params, withCredentials: true }).catch(() => ({ data: { maxes: [] } })),
        ]);
        setMaxes(mRes.data || {});
        setTypedMaxRows(tRes.data?.maxes || []);
        toast.success("Max saved", { description: `${exerciseName} updated` });
    };

    const startFocus = () => {
        setStepIdx(0);
        setPhase("running");
        setSetsCompleted(0);
        setStartedAt(new Date().toISOString());
        setSessionLogged(false);
        setFocusOpen(true);
    };

    // Advance one step. If the next step crosses into a new block AND this
    // is a strength workout, pop the auto-progression wheel first.
    const advanceAfterSet = () => {
        const lastInBlock = isLastStepInBlock(steps, stepIdx);
        if (!lastInBlock) {
            setStepIdx((i) => i + 1);
            return;
        }
        // End of a block. Pop wheel for strength; otherwise jump straight
        // to the next block (or finish). In combined runs the category is
        // decided per-block by its SOURCE workout (a strength + conditioning
        // day only pops the wheel on the strength blocks).
        const finishedCat = steps[stepIdx]
            ? (workout?.blocks?.[steps[stepIdx].blockIdx]?.source_category || workout?.category)
            : workout?.category;
        if (finishedCat === "strength") {
            setPendingDelta(0);
            setPhase("wheel");
        } else {
            advanceToNextBlock();
        }
    };

    const advanceToNextBlock = () => {
        if (stepIdx + 1 >= steps.length) {
            setPhase("done");
            return;
        }
        setStepIdx((i) => i + 1);
        setPhase("running");
    };

    const handleSetComplete = (actual) => {
        if (!currentStep) return;
        const key = `${currentStep.blockIdx}_${currentStep.slotIdx}_${currentStep.setIdx}`;
        setActuals((a) => ({ ...a, [key]: actual }));
        setSetsCompleted((c) => c + 1);
        advanceAfterSet();
    };

    const setLevelForCurrentSlot = async (newLevelIdx) => {
        if (!currentStep) return;
        const block = workout?.blocks?.[currentStep.blockIdx];
        const slot = block?.exercises?.[currentStep.slotIdx];
        const levels = slot?.levels || [];
        if (!levels.length) return;
        const clamped = Math.max(0, Math.min(newLevelIdx, levels.length - 1));
        const baseExId = levels[0]?.exercise_id;
        if (!baseExId) return;
        // Optimistic — apply locally, then persist. If save fails we toast
        // but keep the local choice so the athlete can finish the set.
        setProgression((m) => ({ ...m, [baseExId]: clamped }));
        try {
            await axios.patch(
                `${API}/me/exercise-progression`,
                { base_exercise_id: baseExId, current_level_idx: clamped },
                { params: activeAthleteId ? { athlete_id: activeAthleteId } : {}, withCredentials: true },
            );
            const name = levels[clamped]?.exercise_name || "exercise";
            toast.success("Level saved", { description: `${name} → Level ${clamped + 1}. Carries to all future workouts.` });
        } catch (e) {
            toast.error("Couldn't save level", { description: e?.response?.data?.detail });
        }
    };

    const handleConfirmWheel = async () => {
        // Apply the delta against the DRIVER exercise of the block we just
        // finished — i.e. the first slot of that block (deepest-level driver).
        // For supersets/circuits the wheel uses slot 1 by convention; coaches
        // who care about per-slot progression can move it to its own block.
        const justFinished = currentStep;
        const level = justFinished?.level;
        if (!level) {
            advanceToNextBlock();
            return;
        }
        try {
            const srcWorkoutId = workout?.blocks?.[justFinished?.blockIdx]?.source_workout_id || workoutId;
            const r = await axios.post(
                `${API}/me/weight-intent`,
                {
                    workout_id: srcWorkoutId,
                    exercise_id: level.exercise_id,
                    delta: pendingDelta,
                    based_off_exercise_id: level.based_off_exercise_id || null,
                },
                { params: activeAthleteId ? { athlete_id: activeAthleteId } : {}, withCredentials: true },
            );
            if (r.data.applied && r.data.new_max != null) {
                setMaxes((m) => ({
                    ...m,
                    [level.exercise_id]: { exercise_id: level.exercise_id, value: r.data.new_max, source: "auto_progression" },
                }));
                if (pendingDelta !== 0) {
                    toast.success("1RM updated", { description: `${level.exercise_name} → ${r.data.new_max} lb` });
                }
            }
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail });
        }
        advanceToNextBlock();
    };

    // Log session once when phase=done is reached
    useEffect(() => {
        if (phase !== "done" || sessionLogged || !startedAt) return;
        (async () => {
            try {
                const logIds = isCombined
                    ? sourceWorkouts.map((s) => s.workout_id).filter(Boolean)
                    : [workoutId];
                const completedAt = new Date().toISOString();
                await Promise.all(logIds.map((id) =>
                    axios.post(
                        `${API}/me/workout-sessions`,
                        {
                            workout_id: id,
                            started_at: startedAt,
                            completed_at: completedAt,
                            sets_completed: setsCompleted,
                        },
                        { params: activeAthleteId ? { athlete_id: activeAthleteId } : {}, withCredentials: true },
                    ),
                ));
                setSessionLogged(true);
            } catch (_e) { /* non-fatal */ }
        })();
    }, [phase, sessionLogged, startedAt, setsCompleted, workoutId, isCombined, sourceWorkouts, activeAthleteId]);

    if (loading || !user || loadingData || !workout) {
        return (
            <div className="min-h-screen bg-brand-obsidian flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }

    const elapsedSec = startedAt ? (new Date() - new Date(startedAt)) / 1000 : 0;

    return (
        <div data-testid="workout-runner" className="bg-brand-obsidian min-h-screen pb-20">
            <section className="pt-32 pb-6 border-b border-zinc-900">
                <div className="max-w-4xl mx-auto px-6 md:px-10">
                    <Link to={isCombined ? "/dashboard/workouts" : `/dashboard/workouts/${workoutId}`} className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5 mb-5">
                        <ArrowLeft className="w-3 h-3" /> {isCombined ? "Workouts" : "Workout"}
                    </Link>
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3">
                        {CATEGORY_LABELS[workout.category]} · Run mode
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl tracking-wider leading-[0.95] text-white">
                        {workout.title}
                    </h1>
                    <div className="mt-3 text-xs text-zinc-200 flex items-center gap-4 flex-wrap">
                        <span className="inline-flex items-center gap-1.5"><Layers className="w-3.5 h-3.5 text-brand-red" /> {workout.blocks.length} block{workout.blocks.length === 1 ? "" : "s"}</span>
                        {workout.estimated_duration && <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-brand-red" /> {workout.estimated_duration} min</span>}
                    </div>
                </div>
            </section>

            <section className="py-8">
                <div className="max-w-4xl mx-auto px-6 md:px-10 space-y-6">
                    <BodyweightCard athleteId={activeAthleteId} />
                    <div className="border border-zinc-900 bg-zinc-950 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-1">
                                    Ready to lift
                                </div>
                                <div className="font-display text-2xl tracking-wider text-white">
                                    {phase === "done" ? "Workout complete." : focusOpen ? "Focus Mode is paused." : "Tap Start to enter Focus Mode."}
                                </div>
                                <p className="text-sm text-zinc-200 mt-1">
                                    One block at a time. All sets visible. Plates calculated. Rest timed. Auto-progression between exercises.
                                </p>
                                {missingMaxes.length > 0 && (
                                    <p className="text-[11px] tracking-wider text-amber-300 mt-2">
                                        Tap your max next to the exercise name — prescribed weights recalculate live.
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                data-testid={focusOpen ? "resume-focus" : "start-focus"}
                                onClick={() => focusOpen ? setFocusOpen(true) : startFocus()}
                                disabled={phase === "done"}
                                className="btn-tactical inline-flex disabled:opacity-50"
                            >
                                <Play className="w-4 h-4" /> {focusOpen ? "Resume Focus Mode" : "Start workout"}
                            </button>
                        </div>

                    {/* Read-only map — now shows ALL slots inside supersets/circuits */}
                    <div className="space-y-4">
                        {workout.blocks.map((b, bi) => {
                            const slots = (Array.isArray(b.exercises) && b.exercises.length > 0)
                                ? b.exercises
                                : (Array.isArray(b.levels) && b.levels.length > 0
                                    ? [{ slot: 1, levels: b.levels }] : []);
                            const isLinked = (b.link_type === "superset" || b.link_type === "circuit") && slots.length >= 2;
                            const blockSteps = steps.filter((s) => s.blockIdx === bi);
                            const totalRounds = blockSteps.length > 0 ? blockSteps[0].totalRounds : 0;
                            return (
                                <div key={b.id || bi} className="border border-zinc-900 bg-zinc-950">
                                    <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <span className="font-display text-2xl tracking-wider text-brand-red">{b.name}</span>
                                            {isCombined && b.source_workout_title && (
                                                <span className="text-[9px] tracking-[0.2em] uppercase border border-zinc-700 text-zinc-400 px-2 py-0.5 rounded">
                                                    {b.source_workout_title}
                                                </span>
                                            )}
                                            {isLinked && (
                                                <span className="text-[10px] tracking-[0.25em] uppercase inline-flex items-center gap-1 border border-emerald-600/60 bg-emerald-900/20 text-emerald-200 px-2 py-0.5">
                                                    <Repeat className="w-3 h-3" />
                                                    {b.link_type === "superset" ? "Superset" : "Circuit"} · {totalRounds} set{totalRounds === 1 ? "" : "s"}
                                                </span>
                                            )}
                                            {bi === blockIdx && focusOpen && (
                                                <span className="text-[10px] tracking-[0.25em] uppercase border border-brand-red text-brand-red px-2 py-1 animate-pulse">In progress</span>
                                            )}
                                        </div>
                                    </div>
                                    {b.coach_note && (
                                        <div className="px-5 py-2 border-b border-zinc-900 bg-black/40 flex items-start gap-2 text-xs text-zinc-300">
                                            <MessageSquare className="w-3 h-3 text-brand-red flex-shrink-0 mt-0.5" /> {b.coach_note}
                                        </div>
                                    )}
                                    {/* Sachin 2026-03-05: "When it is a super
                                        set, all of set 1 exercises should show
                                        in a list, then all of set 2." For
                                        supersets/circuits we render the
                                        pre-interleaved `blockSteps` grouped by
                                        round instead of the per-exercise
                                        summary — matches what actually happens
                                        during the workout run. */}
                                    {isLinked ? (() => {
                                        const byRound = new Map();
                                        blockSteps.forEach((s) => {
                                            if (!byRound.has(s.round)) byRound.set(s.round, []);
                                            byRound.get(s.round).push(s);
                                        });
                                        const rounds = Array.from(byRound.keys()).sort((a, b) => a - b);
                                        return rounds.map((round) => (
                                            <div key={`round-${round}`} className="px-5 py-3 border-t border-zinc-900 first:border-t-0">
                                                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-2 flex items-center gap-2">
                                                    <span className="text-brand-red">Set {round}</span>
                                                    <span className="text-zinc-500">/ {totalRounds}</span>
                                                </div>
                                                <div className="space-y-1.5">
                                                    {byRound.get(round).map((s, idx) => {
                                                        const reps = s.set?.reps;
                                                        const load = s.set?.weight || s.set?.load;
                                                        const distance = s.set?.distance;
                                                        const dur = s.set?.duration || s.set?.duration_seconds;
                                                        const chip = [
                                                            reps ? `${reps} reps` : null,
                                                            load ? `${load}` : null,
                                                            distance ? `${distance}` : null,
                                                            dur ? `${dur}s` : null,
                                                        ].filter(Boolean).join(" · ");
                                                        return (
                                                            <div key={idx} className="flex items-baseline gap-3">
                                                                <span className="text-[10px] tracking-[0.2em] uppercase text-brand-red font-semibold w-6 flex-shrink-0">
                                                                    {s.slotLabel}
                                                                </span>
                                                                <span className="text-sm text-white flex-1 truncate">
                                                                    {s.level?.exercise_name || "—"}
                                                                </span>
                                                                {chip && (
                                                                    <span className="text-[11px] text-zinc-300 whitespace-nowrap">{chip}</span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ));
                                    })() : slots.map((slot, slotIdx) => {
                                        const lvl = slot.levels?.[0];
                                        if (!lvl) return null;
                                        const slotLabel = (b.block_group || "?").charAt(0).toUpperCase();
                                        return (
                                            <div key={slotIdx} className="px-5 py-3 border-t border-zinc-900 first:border-t-0">
                                                <div className="flex items-center justify-between gap-3">
                                                    <div>
                                                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 flex items-center gap-2">
                                                            <span className="text-brand-red">{slotLabel}</span>
                                                            <span>Level {lvl.level}</span>
                                                        </div>
                                                        <div className="font-display text-xl tracking-wider text-white">{lvl.exercise_name}</div>
                                                    </div>
                                                    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300">
                                                        {lvl.sets_data?.length || 0} sets
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {focusOpen && currentStep && (
                <FocusModeOverlay
                    workout={workout}
                    step={currentStep}
                    steps={steps}
                    stepIdx={stepIdx}
                    actuals={actuals}
                    setActuals={setActuals}
                    phase={phase}
                    pendingDelta={pendingDelta}
                    setPendingDelta={setPendingDelta}
                    maxes={maxes}
                    equipmentMap={equipmentMap}
                    exerciseMap={exerciseMap}
                    onClose={() => setFocusOpen(false)}
                    onSetComplete={handleSetComplete}
                    onSkipRest={() => {}}
                    onConfirmWheel={handleConfirmWheel}
                    onChangeLevel={setLevelForCurrentSlot}
                    onSaveMax={saveMaxInline}
                    typedMaxes={typedMaxes}
                    onSaveTypedMax={saveTypedMax}
                    sessionStats={{ setsCompleted, elapsedSec }}
                    startedAt={startedAt}
                />
            )}
        </div>
    );
}
